/**
 * PhilosophyTag.tsx — inline icon mark / badge for philosophy principles.
 *
 * Lighter-weight than <philosophyCallout>. Used inline in a paragraph or as a
 * small flourish before a section heading — marks "this is one of Lars's principles"
 * without taking a whole boxed callout.
 *
 * Two variants:
 *   A. Icon-only (no label prop): 18×18 icon inline with text, aria-hidden. Decorative.
 *   B. Icon + label (label prop set): small pill-badge with icon + text.
 *
 * CSS: .blog-philosophy-tag and .blog-philosophy-tag--* in globals.css
 *   Badge background: --cream-3 (#E2D9CE) — matches callout/timeline disc family.
 *   Label: --font-sans, 13px literal, 600, --text-dark-muted.
 *   Flow: display:inline-flex; vertical-align:middle — sits inline without disrupting baseline.
 *
 * Icon rule: inline SVG only — kit §12 §4 v1.4.
 * Icon size: 18×18 inline text / 20×20 badge variant.
 *
 * Usage in MDX:
 *   <philosophyTag icon="inner-pulse" />
 *   <philosophyTag icon="wrist-freedom" label="Wrist Freedom" />
 *
 * Props:
 *   icon  — one of the 10 approved PhilosophyIconName values. Required.
 *   label — string. Optional. If omitted, renders icon-only (variant A).
 *
 * Added 2026-05-09 per Aria component spec (video-blog migrations cycle 5).
 */

import PhilosophyIcon, { type PhilosophyIconName } from './PhilosophyIcon';

interface PhilosophyTagProps {
  icon: PhilosophyIconName;
  label?: string;
}

export default function PhilosophyTag({ icon, label }: PhilosophyTagProps) {
  if (label) {
    // Variant B — icon + label badge
    return (
      <span className="blog-philosophy-tag blog-philosophy-tag--with-label">
        <span aria-hidden="true">
          <PhilosophyIcon name={icon} width={20} height={20} />
        </span>
        <span className="blog-philosophy-tag__label">{label}</span>
      </span>
    );
  }

  // Variant A — icon-only (decorative; surrounding text provides context)
  return (
    <span className="blog-philosophy-tag" aria-hidden="true">
      <PhilosophyIcon name={icon} width={18} height={18} />
    </span>
  );
}
